import { FeatherIcon } from '../../FeatherIcon';
import type { SalesMediaItem } from '../../../api/client';

/**
 * True when a catalogue item is a video (or gif) rather than a photo. Mirrors the
 * type-filter test in ContentPickerModal so the tag and the filter never disagree.
 */
export function isVideoItem(type: string | null | undefined): boolean {
  const t = (type || '').toLowerCase();
  return t.includes('video') || t.includes('gif');
}

/** `mm:ss` clip length, or '' when the item carries no usable duration. */
export function fmtDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || seconds <= 0) return '';
  const total = Math.round(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/**
 * A small top-left pill overlaid on a media thumbnail: a photo/video type tag,
 * plus the clip length for videos. The parent must be `position: relative`.
 * `size` scales the pill for the small pinned tiles (`sm`) vs the picker grid (`md`).
 */
export function MediaBadge({
  item,
  size = 'md',
}: {
  item: Pick<SalesMediaItem, 'type' | 'duration'>;
  size?: 'sm' | 'md';
}) {
  const video = isVideoItem(item.type);
  const dur = video ? fmtDuration(item.duration) : '';
  const iconSize = size === 'sm' ? 9 : 11;
  return (
    <span
      style={{
        position: 'absolute',
        top: size === 'sm' ? 3 : 5,
        left: size === 'sm' ? 3 : 5,
        display: 'inline-flex',
        alignItems: 'center',
        gap: dur ? 3 : 0,
        padding: dur ? (size === 'sm' ? '1px 4px 1px 3px' : '2px 6px 2px 4px') : size === 'sm' ? 2 : 3,
        borderRadius: 999,
        background: 'rgba(0,0,0,.62)',
        color: '#fff',
        fontSize: size === 'sm' ? 8.5 : 10,
        fontWeight: 700,
        lineHeight: 1,
        fontVariantNumeric: 'tabular-nums',
        maxWidth: 'calc(100% - 6px)',
        pointerEvents: 'none',
      }}
    >
      <FeatherIcon name={video ? 'video' : 'image'} size={iconSize} />
      {dur && <span>{dur}</span>}
    </span>
  );
}
